import {Box, Tab, Tabs} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {setShowModal} from '../../../../redux/slices/unSaveChangesModalSlice';
import AurBreadcrumb from '../../../common/aur-breadcrumb/AurBreadCrumb';
import {UploadDragAndDropTab} from './UploadDragAndDropTab';
import {
  UploadHighSpeedTab,
  UploadHighSpeedTabHandler,
} from './UploadHighSpeedTab';
import LeaveFileCatalystConfirmationDialog from './components/LeaveFileCatalystConfirmationDialog';
import AurBanner from '../../../common/aur-banner/AurBanner';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { GA_CLICK_TYPES, GA_EVENT_NAMES, GA_REGIONS, GA_SUBPAGE_TYPE } from '../../../../constants/analyticsConstants';

export type LeaveActionType = 'high-speed' | 'upload-new-evidence-source';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = ({children, value, index}: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{display: value === index ? 'block' : 'none'}} // keep mounted
    >
      <Box>{children}</Box>
    </div>
  );
};

function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

const UploadFileCatalyst = () => {
  const uploadHighSpeedTabRef = useRef<UploadHighSpeedTabHandler | null>(null);
  const [leaveFrom, setLeaveFrom] = useState<LeaveActionType>();
  const [activeTab, setActiveTab] = useState(0);
  const [isFCAgentRunning, setIsFCAgentRunning] = useState(false);
  const [uploadDragDropFileCount, setUploadDragDropFileCount] =
    useState<number>(0);
  const [uploadHighSpeedFileCount, setUploadHighSpeedFileCount] =
    useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
      const {trackGAEvent} = useAnalytics();
  

  const uploadFileCount = useMemo(
    () => uploadDragDropFileCount + uploadHighSpeedFileCount,
    [uploadDragDropFileCount, uploadHighSpeedFileCount]
  );

  const isFCAgentNotRunning = useMemo(
    () => activeTab === 1 && !isFCAgentRunning,
    [activeTab, isFCAgentRunning]
  );

  const showLeaveModal = useCallback(() => {
    if (uploadFileCount > 0) {
      dispatch(setShowModal(true));
      return true;
    }
    return false;
  }, [dispatch, uploadFileCount]);

  const breadCrumbItems = useMemo(() => {
    return [
      {
        label: t('uploadEvidence.newEvidence'),
        path: '#',
        onClick: () => {
          const isShowModal = showLeaveModal();
          if (isShowModal) {
            setLeaveFrom('upload-new-evidence-source');
          } else {
            localStorage?.setItem('currentPath', '');
            navigate('/upload-evidence/new-evidence');
          }
        },
      },
      {
        label:
          activeTab === 0
            ? t('fileCatalyst.dragAndDropUpload')
            : t('fileCatalyst.highSpeedUpload'),
      },
    ];
  }, [activeTab, navigate, showLeaveModal]);

  useEffect(() => {
    const copiedUploadHighSpeedTabRef = uploadHighSpeedTabRef.current;
    localStorage.setItem('currentPath', '/upload-file-catalyst');
    return () => {
      copiedUploadHighSpeedTabRef?.cancelPendingUpload();
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
trackGAEvent({
                                               eventName: GA_EVENT_NAMES.CLICK,
                                               click: {
                                                 name: newValue == 1 ? t('gaTracking.uploadEvidence.highspeedUpload') : t('gaTracking.uploadEvidence.dndUpload'),
                                                 type: GA_CLICK_TYPES.TAB,
                                                 region: GA_REGIONS.BODY,
                                                 action: t('gaTracking.uploadEvidence.uploadFileTabSwitch'),
                                               },
                                               subPage: {
                                                subpage_name : newValue == 1 ? t('gaTracking.uploadEvidence.dndUpload') : t('gaTracking.uploadEvidence.highspeedUpload'),
                                                subpage_type : GA_SUBPAGE_TYPE.TAB
                                               }
                                             });
  };

  const handleOnCloseLeaveModal = (isLeave: boolean) => {
    if (isLeave) {
      if (leaveFrom === 'high-speed') {
        setActiveTab(1);
      } else {
        navigate('/upload-evidence/new-evidence');
      }
    }
    setLeaveFrom(undefined);
  };

  return (
    <Box className={'file-catalyst-container'}>
      {isFCAgentNotRunning ? (
        <Box className={'banner-container'}>
          <AurBanner
            errorText={t('fileCatalyst.highSpeedUploads.fcBannerMessage')}
            errorType={'info'}
            showIcon
            hasFullWidth
          />
        </Box>
      ) : null}
      <Box className={'file-catalyst-header'}>
        <AurBreadcrumb items={breadCrumbItems} />
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <Tabs
            data-testid="Tabs"
            value={activeTab}
            onChange={handleChange}
            aria-label=""
            className="aur-tabs user-page-tabs"
          >
            <Tab
              data-testid="drag-and-drop-tab"
              label={t('fileCatalyst.dragAndDropUpload')}
              {...a11yProps(0)}
              tabIndex={0}
            />
            <Tab
              data-testid="high-speed-tab"
              label={t('fileCatalyst.highSpeedUpload')}
              {...a11yProps(1)}
              tabIndex={0}
            />
          </Tabs>
        </Box>
      </Box>
      <CustomTabPanel value={activeTab} index={0}>
        <UploadDragAndDropTab
          onChangeUploadFileCount={setUploadDragDropFileCount}
        />
      </CustomTabPanel>
      <CustomTabPanel value={activeTab} index={1}>
        <UploadHighSpeedTab
          ref={uploadHighSpeedTabRef}
          isActiveTab={activeTab === 1}
          onChangeIsFCAgentRunning={setIsFCAgentRunning}
          onChangeUploadFileCount={setUploadHighSpeedFileCount}
        />
      </CustomTabPanel>
      <LeaveFileCatalystConfirmationDialog
        files={uploadFileCount}
        isShowLeaveModalFrom={leaveFrom}
        onLeavePageConfirm={handleOnCloseLeaveModal}
      />
    </Box>
  );
};

export {UploadFileCatalyst};
